// Self-contained QR Code encoder (ISO/IEC 18004), byte mode with UTF-8.
//
// The site ships no QR dependency and the tool must keep working offline, so
// the encoder lives here: data segment → Reed-Solomon error correction →
// interleaving → module placement → mask selection.
//
// Byte mode covers every input the tool accepts; numeric and alphanumeric
// modes would only shrink the symbol, never change what it decodes to.

export const ERROR_LEVELS = ['L', 'M', 'Q', 'H'];

// Format-field values, which are *not* the same as the table order below.
const FORMAT_BITS = { L: 1, M: 0, Q: 3, H: 2 };
const LEVEL_INDEX = { L: 0, M: 1, Q: 2, H: 3 };

// Error-correction codewords per block, indexed [level][version].
const ECC_CODEWORDS_PER_BLOCK = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
];

// Number of error-correction blocks, indexed [level][version].
const NUM_ERROR_CORRECTION_BLOCKS = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
];

const MIN_VERSION = 1;
const MAX_VERSION = 40;
const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

const getBit = (value, index) => ((value >>> index) & 1) !== 0;

/** Total data modules (bits) available before error correction is subtracted. */
const rawDataModules = (version) => {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) result -= 36; // two 6×3 version-information blocks
  }
  return result;
};

const rawCodewords = (version) => Math.floor(rawDataModules(version) / 8);

const dataCodewords = (version, level) => {
  const ecl = LEVEL_INDEX[level];
  return rawCodewords(version)
    - ECC_CODEWORDS_PER_BLOCK[ecl][version] * NUM_ERROR_CORRECTION_BLOCKS[ecl][version];
};

export const toUtf8Bytes = (text) => {
  if (typeof TextEncoder !== 'undefined') return Array.from(new TextEncoder().encode(text));
  return Array.from(unescape(encodeURIComponent(text)), (ch) => ch.charCodeAt(0));
};

// ─── Reed-Solomon over GF(2^8), primitive polynomial x^8 + x^4 + x^3 + x^2 + 1 ──

const gfMultiply = (x, y) => {
  let z = 0;
  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
};

/** Coefficients of (x - r^0)(x - r^1)…(x - r^(degree-1)), highest power omitted. */
const rsDivisor = (degree) => {
  const result = new Uint8Array(degree);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < degree; j += 1) {
      result[j] = gfMultiply(result[j], root);
      if (j + 1 < degree) result[j] ^= result[j + 1];
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
};

const rsRemainder = (data, divisor) => {
  const result = new Uint8Array(divisor.length);
  data.forEach((byte) => {
    const factor = byte ^ result[0];
    result.copyWithin(0, 1);
    result[result.length - 1] = 0;
    for (let i = 0; i < divisor.length; i += 1) {
      result[i] ^= gfMultiply(divisor[i], factor);
    }
  });
  return Array.from(result);
};

// ─── Bit stream ────────────────────────────────────────────────────────────

const charCountBits = (version) => (version <= 9 ? 8 : 16);

const buildCodewords = (bytes, version, level) => {
  const bits = [];
  const append = (value, length) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
  };

  append(0b0100, 4); // byte mode
  append(bytes.length, charCountBits(version));
  bytes.forEach((byte) => append(byte, 8));

  const capacityBits = dataCodewords(version, level) * 8;
  append(0, Math.min(4, capacityBits - bits.length)); // terminator
  append(0, (8 - (bits.length % 8)) % 8); // pad to a whole codeword

  const codewords = [];
  for (let i = 0; i < bits.length; i += 8) {
    codewords.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  }
  // Alternating pad codewords fill the rest of the capacity.
  for (let pad = 0xec; codewords.length < capacityBits / 8; pad ^= 0xec ^ 0x11) {
    codewords.push(pad);
  }
  return codewords;
};

/** Splits data into blocks, appends each block's ECC, then interleaves both. */
const addEccAndInterleave = (data, version, level) => {
  const ecl = LEVEL_INDEX[level];
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[ecl][version];
  const blockEccLen = ECC_CODEWORDS_PER_BLOCK[ecl][version];
  const total = rawCodewords(version);
  const numShortBlocks = numBlocks - (total % numBlocks);
  const shortBlockLen = Math.floor(total / numBlocks);

  const divisor = rsDivisor(blockEccLen);
  const blocks = [];
  for (let i = 0, offset = 0; i < numBlocks; i += 1) {
    const length = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1);
    const block = data.slice(offset, offset + length);
    offset += length;
    const ecc = rsRemainder(block, divisor);
    // Short blocks get a placeholder so every block indexes alike below.
    if (i < numShortBlocks) block.push(0);
    blocks.push(block.concat(ecc));
  }

  const result = [];
  for (let i = 0; i < blocks[0].length; i += 1) {
    blocks.forEach((block, j) => {
      // Skips the placeholder byte that short blocks do not really have.
      if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) result.push(block[i]);
    });
  }
  return result;
};

// ─── Symbol drawing ────────────────────────────────────────────────────────

const alignmentPositions = (version) => {
  if (version === 1) return [];
  const numAlign = Math.floor(version / 7) + 2;
  const size = version * 4 + 17;
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const positions = [6];
  for (let pos = size - 7; positions.length < numAlign; pos -= step) positions.splice(1, 0, pos);
  return positions;
};

class Symbol {
  constructor(version) {
    this.version = version;
    this.size = version * 4 + 17;
    this.modules = Array.from({ length: this.size }, () => new Array(this.size).fill(false));
    this.isFunction = Array.from({ length: this.size }, () => new Array(this.size).fill(false));
  }

  setFunction(x, y, isDark) {
    this.modules[y][x] = isDark;
    this.isFunction[y][x] = true;
  }

  drawFinder(x, y) {
    for (let dy = -4; dy <= 4; dy += 1) {
      for (let dx = -4; dx <= 4; dx += 1) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const px = x + dx;
        const py = y + dy;
        if (px >= 0 && px < this.size && py >= 0 && py < this.size) {
          this.setFunction(px, py, dist !== 2 && dist !== 4);
        }
      }
    }
  }

  drawAlignment(x, y) {
    for (let dy = -2; dy <= 2; dy += 1) {
      for (let dx = -2; dx <= 2; dx += 1) {
        this.setFunction(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }

  drawFormatBits(level, mask) {
    const data = (FORMAT_BITS[level] << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((data << 10) | rem) ^ 0x5412; // BCH(15,5) with the spec's mask

    for (let i = 0; i <= 5; i += 1) this.setFunction(8, i, getBit(bits, i));
    this.setFunction(8, 7, getBit(bits, 6));
    this.setFunction(8, 8, getBit(bits, 7));
    this.setFunction(7, 8, getBit(bits, 8));
    for (let i = 9; i < 15; i += 1) this.setFunction(14 - i, 8, getBit(bits, i));

    for (let i = 0; i < 8; i += 1) this.setFunction(this.size - 1 - i, 8, getBit(bits, i));
    for (let i = 8; i < 15; i += 1) this.setFunction(8, this.size - 15 + i, getBit(bits, i));
    this.setFunction(8, this.size - 8, true); // the always-dark module
  }

  drawVersionBits() {
    if (this.version < 7) return;
    let rem = this.version;
    for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (this.version << 12) | rem; // BCH(18,6)

    for (let i = 0; i < 18; i += 1) {
      const bit = getBit(bits, i);
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.setFunction(a, b, bit);
      this.setFunction(b, a, bit);
    }
  }

  drawFunctionPatterns(level) {
    for (let i = 0; i < this.size; i += 1) {
      this.setFunction(6, i, i % 2 === 0);
      this.setFunction(i, 6, i % 2 === 0);
    }

    this.drawFinder(3, 3);
    this.drawFinder(this.size - 4, 3);
    this.drawFinder(3, this.size - 4);

    const positions = alignmentPositions(this.version);
    const last = positions.length - 1;
    positions.forEach((y, i) => {
      positions.forEach((x, j) => {
        // The three corners already carry finder patterns.
        const isCorner = (i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0);
        if (!isCorner) this.drawAlignment(x, y);
      });
    });

    this.drawFormatBits(level, 0); // placeholder, rewritten once the mask is picked
    this.drawVersionBits();
  }

  /** Zigzags the codewords up and down two-module-wide columns, skipping column 6. */
  drawCodewords(data) {
    let i = 0;
    for (let right = this.size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < this.size; vert += 1) {
        for (let j = 0; j < 2; j += 1) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? this.size - 1 - vert : vert;
          if (!this.isFunction[y][x] && i < data.length * 8) {
            this.modules[y][x] = getBit(data[i >>> 3], 7 - (i & 7));
            i += 1;
          }
          // Remaining modules stay light, as the spec's remainder bits.
        }
      }
    }
  }

  /** XOR-applies a mask; calling it twice with the same mask undoes it. */
  applyMask(mask) {
    const conditions = [
      (x, y) => (x + y) % 2 === 0,
      (x, y) => y % 2 === 0,
      (x) => x % 3 === 0,
      (x, y) => (x + y) % 3 === 0,
      (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
      (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
      (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
      (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
    ];
    const invert = conditions[mask];

    for (let y = 0; y < this.size; y += 1) {
      for (let x = 0; x < this.size; x += 1) {
        if (!this.isFunction[y][x] && invert(x, y)) this.modules[y][x] = !this.modules[y][x];
      }
    }
  }

  /** The spec's four penalty rules; the lowest-scoring mask wins. */
  penaltyScore() {
    let result = 0;
    const { size, modules } = this;

    // Rule 1: runs of five or more same-coloured modules in a row or column.
    // Runs start light with length 0 so the quiet zone counts as the leading
    // light border, which rule 3 below needs to spot edge finder patterns.
    for (let y = 0; y < size; y += 1) {
      let runColor = false;
      let runLength = 0;
      const runHistory = new Array(7).fill(0);
      for (let x = 0; x < size; x += 1) {
        if (modules[y][x] === runColor) {
          runLength += 1;
          if (runLength === 5) result += PENALTY_N1;
          else if (runLength > 5) result += 1;
        } else {
          this.finderPenaltyAddHistory(runLength, runHistory);
          if (!runColor) result += this.finderPenaltyCountPatterns(runHistory) * PENALTY_N3;
          runColor = modules[y][x];
          runLength = 1;
        }
      }
      result += this.finderPenaltyTerminateAndCount(runColor, runLength, runHistory) * PENALTY_N3;
    }
    for (let x = 0; x < size; x += 1) {
      let runColor = false;
      let runLength = 0;
      const runHistory = new Array(7).fill(0);
      for (let y = 0; y < size; y += 1) {
        if (modules[y][x] === runColor) {
          runLength += 1;
          if (runLength === 5) result += PENALTY_N1;
          else if (runLength > 5) result += 1;
        } else {
          this.finderPenaltyAddHistory(runLength, runHistory);
          if (!runColor) result += this.finderPenaltyCountPatterns(runHistory) * PENALTY_N3;
          runColor = modules[y][x];
          runLength = 1;
        }
      }
      result += this.finderPenaltyTerminateAndCount(runColor, runLength, runHistory) * PENALTY_N3;
    }

    // Rule 2: 2×2 blocks of one colour.
    for (let y = 0; y < size - 1; y += 1) {
      for (let x = 0; x < size - 1; x += 1) {
        const color = modules[y][x];
        if (color === modules[y][x + 1] && color === modules[y + 1][x]
          && color === modules[y + 1][x + 1]) {
          result += PENALTY_N2;
        }
      }
    }

    // Rule 4: deviation of dark modules from 50%.
    const dark = modules.reduce((sum, row) => sum + row.filter(Boolean).length, 0);
    const total = size * size;
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
    result += k * PENALTY_N4;

    return result;
  }

  // Rule 3 helpers: the 1:1:3:1:1 finder-like pattern with a 4-module gap.
  finderPenaltyAddHistory(currentRunLength, runHistory) {
    let run = currentRunLength;
    if (runHistory[0] === 0) run += this.size; // treat the border as light
    runHistory.copyWithin(1, 0);
    runHistory[0] = run;
  }

  finderPenaltyCountPatterns(runHistory) {
    const n = runHistory[1];
    const core = n > 0 && runHistory[2] === n && runHistory[3] === n * 3
      && runHistory[4] === n && runHistory[5] === n;
    return (core && runHistory[0] >= n * 4 && runHistory[6] >= n ? 1 : 0)
      + (core && runHistory[6] >= n * 4 && runHistory[0] >= n ? 1 : 0);
  }

  finderPenaltyTerminateAndCount(currentRunColor, currentRunLength, runHistory) {
    let run = currentRunLength;
    if (currentRunColor) {
      this.finderPenaltyAddHistory(run, runHistory);
      run = 0;
    }
    run += this.size; // the border counts as light
    this.finderPenaltyAddHistory(run, runHistory);
    return this.finderPenaltyCountPatterns(runHistory);
  }
}

const smallestVersion = (byteLength, level) => {
  for (let version = MIN_VERSION; version <= MAX_VERSION; version += 1) {
    const capacity = dataCodewords(version, level) * 8;
    if (4 + charCountBits(version) + byteLength * 8 <= capacity) return version;
  }
  return null;
};

/**
 * Encodes `text` and returns { size, modules, version, level }, where
 * `modules[y][x]` is true for a dark module. Throws on empty or oversized input.
 * `forcedMask` pins one of the eight masks instead of picking the best-scoring
 * one — every mask decodes identically, so this only matters for testing.
 */
export const encodeQr = (text, level = 'M', forcedMask = null) => {
  if (!ERROR_LEVELS.includes(level)) throw new Error('qr-level');
  const value = String(text ?? '');
  if (!value) throw new Error('qr-empty');

  const bytes = toUtf8Bytes(value);
  const version = smallestVersion(bytes.length, level);
  if (version === null) throw new Error('qr-too-long');

  const codewords = addEccAndInterleave(buildCodewords(bytes, version, level), version, level);

  const symbol = new Symbol(version);
  symbol.drawFunctionPatterns(level);
  symbol.drawCodewords(codewords);

  let bestMask = forcedMask;
  if (bestMask === null) {
    let bestScore = Infinity;
    for (let mask = 0; mask < 8; mask += 1) {
      symbol.applyMask(mask);
      symbol.drawFormatBits(level, mask);
      const score = symbol.penaltyScore();
      if (score < bestScore) {
        bestScore = score;
        bestMask = mask;
      }
      symbol.applyMask(mask); // XOR again to undo
    }
  }
  symbol.applyMask(bestMask);
  symbol.drawFormatBits(level, bestMask);

  return {
    size: symbol.size,
    modules: symbol.modules,
    version,
    level,
    mask: bestMask,
  };
};

/** Largest byte payload a level can hold, used for the tool's live counter. */
export const capacityBytes = (level) => dataCodewords(MAX_VERSION, level)
  - Math.ceil((4 + charCountBits(MAX_VERSION)) / 8);

/** Renders an encoded symbol as a standalone SVG string (4-module quiet zone). */
export const toSvg = (qr, { scale = 8, margin = 4, dark = '#000000', light = '#ffffff' } = {}) => {
  const dimension = (qr.size + margin * 2) * scale;
  const path = [];
  for (let y = 0; y < qr.size; y += 1) {
    for (let x = 0; x < qr.size; x += 1) {
      if (qr.modules[y][x]) path.push(`M${(x + margin) * scale} ${(y + margin) * scale}h${scale}v${scale}h-${scale}z`);
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dimension} ${dimension}" width="${dimension}" height="${dimension}" shape-rendering="crispEdges">`
    + `<rect width="${dimension}" height="${dimension}" fill="${light}"/>`
    + `<path d="${path.join('')}" fill="${dark}"/></svg>`;
};
